import { useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { resolveHallReturnRoute } from "@/lib/hallReturnRoute.mjs";
import { ChevronRight } from "lucide-react";

// Clean door photograph (no baked text, no button) — approved reference.
const DOOR_IMAGE =
  "https://customer-assets-rejwkqb3.emergentagent.net/job_4fdfd6f9-6803-4276-8258-f36ae827b1f3/artifacts/pntpjlxa_79B8E46A-C20C-47B8-AD98-722E2F90A324.png";

export default function WelcomeDoor() {
  const nav = useNavigate();
  const { user, initializing, logout } = useAuth();

  // Public-facing tab title for the entry door.
  useEffect(() => {
    const prev = document.title;
    document.title = "Anokina Studio — Лаборатория превращений";
    return () => { document.title = prev; };
  }, []);

  const isAuthed = !!user;
  const label = isAuthed ? "Выйти" : "Войти";
  const returnTarget = resolveHallReturnRoute({
    storage: window.sessionStorage,
  });

  const onClick = () => {
    if (initializing) return;
    if (isAuthed) {
      // Sign out and stay on the door — button will flip back to «Войти».
      logout();
      return;
    }
    nav("/login");
  };

  return (
    <div
      className="fixed inset-0 w-full h-full overflow-hidden"
      data-testid="welcome-door-page"
      style={{ background: "#efe6d5" }}
    >
      {/* Full-bleed hero — image fills the entire viewport on mobile AND desktop. */}
      <div className="relative w-full h-full overflow-hidden">
        <img
          src={DOOR_IMAGE}
          alt=""
          aria-hidden="true"
          className="absolute inset-0 w-full h-full object-cover"
          style={{ objectPosition: "50% 45%" }}
          data-testid="welcome-door-image"
        />

        {/* Dark vignette so white text reads over the warm scene */}
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            background:
              "radial-gradient(120% 90% at 50% 45%, rgba(0,0,0,0) 0%, rgba(0,0,0,0.28) 60%, rgba(0,0,0,0.58) 100%), linear-gradient(180deg, rgba(0,0,0,0.20) 0%, rgba(0,0,0,0) 25%, rgba(0,0,0,0.30) 100%)",
          }}
        />

        {isAuthed && !initializing && (
          <button
            type="button"
            onClick={() => nav(returnTarget, { replace: true })}
            data-testid="entrance-return-button"
            aria-label="Назад в Anokina Studio"
            title="Назад в Anokina Studio"
            className="absolute z-20 top-3 right-3 md:top-5 md:right-5 min-h-[40px] px-3.5 rounded-lg bg-white/88 backdrop-blur border border-white/55 flex items-center gap-2 hover:bg-white transition-colors shadow-[0_2px_10px_rgba(0,0,0,0.20)]"
          >
            <span className="font-serif text-[13px] text-[#3A2B22] whitespace-nowrap">
              Назад в Anokina Studio
            </span>
            <ChevronRight className="w-4 h-4 text-[#4A3B32] shrink-0" strokeWidth={1.6} />
          </button>
        )}

        {/* Overlay content */}
        <div className="relative z-10 h-full w-full flex flex-col items-center px-6 sm:px-8 pt-16 sm:pt-20 pb-12">
          <div
            className="text-[10px] sm:text-[11px] tracking-[0.40em] text-white/95"
            style={{ textShadow: "0 1px 8px rgba(0,0,0,0.35)" }}
            data-testid="welcome-brand"
          >
            OKSANA ANOKINA
          </div>

          <h1
            className="mt-6 sm:mt-8 font-serif text-white leading-[1.05] text-center text-[30px] sm:text-[38px]"
            style={{ letterSpacing: "-0.005em", textShadow: "0 2px 14px rgba(0,0,0,0.35)" }}
            data-testid="welcome-title"
          >
            Лаборатория
            <br />
            превращений
          </h1>

          <div
            className="mt-3 sm:mt-4 font-serif italic text-white/95 text-[14px] sm:text-base text-center"
            style={{ textShadow: "0 1px 8px rgba(0,0,0,0.35)" }}
            data-testid="welcome-subtitle"
          >
            Дом знаний, вкуса и ремесла
          </div>

          {/* Authenticated users return to the Hall; anonymous users enter login. */}
          <button
            type="button"
            onClick={onClick}
            data-testid="welcome-enter-button"
            aria-label={label}
            className="mt-auto relative inline-flex items-center justify-center h-[46px] sm:h-[50px] rounded-xl backdrop-blur-md transition-colors hover:bg-white/15"
            style={{
              width: "min(74vw, 280px)",
              background: "rgba(255,255,255,0.10)",
              border: "1px solid rgba(255,255,255,0.55)",
              boxShadow: "0 2px 14px rgba(0,0,0,0.20)",
            }}
          >
            <span className="font-serif text-white text-[15px] sm:text-base tracking-[0.06em]">
              {label}
            </span>
          </button>
          {!isAuthed && !initializing && (
            <Link to="/register"
              data-testid="welcome-register-link"
              className="mt-3 text-center font-serif text-sm text-white underline underline-offset-4 decoration-white/75 focus-visible:outline focus-visible:outline-2 focus-visible:outline-white"
              style={{ textShadow: "0 1px 8px rgba(0,0,0,0.45)" }}>
              Впервые здесь? Зарегистрироваться
            </Link>
          )}
        </div>
      </div>
    </div>
  );
}
